import { Component, OnInit, Output, EventEmitter } from "@angular/core";
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
} from "@angular/forms";
import { IBasicUserSettingsModel } from "../shared/models/basicSettingsModel";
import { UserService } from "../shared/services/user.service";
import { Router } from "@angular/router";
import { NotificationService } from "@progress/kendo-angular-notification";
import { PasswordResetRequestModel } from "../shared/models/passwordResetRequestModel";
import { ToastService } from "src/app/shared/services/toast.service";
import { HomeLocation } from "../shared/models/homeLocationsModel";
import { UserValidationErrors } from "../shared/models/userValidationsErrors.enum";
import { Subscription } from "rxjs";
import { Store } from "@ngrx/store";
import { State } from "src/app/state/app.state";
import {
  getACLicenseStatus,
  getLoggedInUserHomeLocationInfo,
  getLoggedInUserId,
  GetUserPermissionList,
} from "src/app/state";
import { NgxSpinnerService } from "ngx-spinner";
import { MasterLookupService } from "../../../master/master-services/master-lookup.service";
import { IDropdownSettings } from "ng-multiselect-dropdown";
import { MasterMyProfileService } from "../../../master/master-services/master-my-profile.service";
import { PermissionValidatorService } from "../shared/services/permission-validator.service";
import { LocationSelectorInfo } from "../shared/models/locationSelectorModel";
import EmailValidator from "src/app/shared/validators/generic-email-validator";
import { IdentityServiceService } from "../../system-configuration/shared/services/identity-service.service";
import { TenantConfiguration } from "../../system-configuration/shared/models/tenant-configuration";
import { TranslationLoaderFacade } from "../../localization/translation-loader.facade";
import { TranslateService } from "@ngx-translate/core";
import { PermissionGroup } from "src/app/master/models/permissionGroup";
import { NotificationGroup } from "src/app/master/models/NotificationGroup";
import { UserEditDetails } from "../shared/models/userEditDetailsModel";
import { IListItemModel } from "../shared/models/listItemModel";

@Component({
  selector: "app-user-settings",
  templateUrl: "./user-settings.component.html",
  styleUrls: ["./user-settings.component.scss"],
})
export class UserSettingsComponent implements OnInit {
  @Output() submittedEmitter = new EventEmitter<boolean>(true);
  @Output() dirtyEmitter = new EventEmitter<boolean>(false);
  @Output() closeEmitter = new EventEmitter();
  @Output() asyncUserUpdateEmitter = new EventEmitter();

  updateManageUserSettingPage() {
    this.asyncUserUpdateEmitter.emit();
  }

  public basicSettings: FormGroup = new FormGroup({
    userCode: new FormControl(),
    firstName: new FormControl(),
    lastName: new FormControl(),
    userName: new FormControl(),
    userGroup: new FormControl(),
    activeUser: new FormControl(),
    supervisor: new FormControl(),
    phoneNumber: new FormControl(),
    loginEnabled: new FormControl(),
    permissionGroup: new FormControl(),
    notificationGroups: new FormControl(),
    prefferedLanguage: new FormControl(),
    spanishSpeaker: new FormControl(),
    accountLocked: new FormControl(),
    resetAtNextLogon: new FormControl(),
    useAuthentication: new FormControl(),
    primaryNotificationEmail: new FormControl(),
    active: new FormControl(),
  });

  public allUserData: UserEditDetails | null = null;
  public supervisorList = new Array();
  public userPermissionList: number[] = [];
  public locationList: HomeLocation[] = [];
  public permissionGroupList: PermissionGroup[] = [];
  public notificationGroupList: NotificationGroup[] = [];
  public selectedNotificationGroupList: NotificationGroup[] = [];

  public userActive: boolean = false;
  public isResetClicked: boolean = false;
  public userloginEnabled: boolean = false;
  public failedLoginCount: number = 0;
  public loggedUserId: number | null = null;

  public invalidPhoneNumber: boolean = false;
  public userCodeAlreadyUsed: boolean = false;
  public userNameAlreadyUsed: boolean = false;
  public editingUser: UserEditDetails | null = null;
  public permissionGroupId: number | null = null;
  public editingUserId: number | undefined;
  public locationId: number = 0;
  public passwordResetLink: string = "";
  public locationName: string = "";
  public show: boolean = false;
  public isFormDirty: boolean = false;
  public isACLicenseEnabled: boolean = false;
  public isSaveAndContinueVisible: boolean = false;
  public tenantConfiguration: TenantConfiguration | undefined;
  public primaryNotificationEmailAlreadyUsed: boolean | undefined;

  public allText: string = "";
  public noDataText: string = "";

  public prefferedLanguagesAvailable: IListItemModel[] = [
    { id: 1, displayValue: "English " },
    { id: 3, displayValue: "Spanish" },
  ];
  public dropdownSettings: IDropdownSettings = {};

  private subscriptions = new Subscription();

  constructor(
    public fb: FormBuilder,
    private router: Router,
    private store: Store<State>,
    private spinner: NgxSpinnerService,
    private userService: UserService,
    public toastService: ToastService,
    private lookupService: MasterLookupService,
    private myProfileService: MasterMyProfileService,
    private notificationService: NotificationService,
    private permissionvalidator: PermissionValidatorService,
    private tenantConfigService: IdentityServiceService,
    private translationLoaderFacade: TranslationLoaderFacade,
    private translateService: TranslateService,
  ) {
    const acLicenseSubscription = this.store
      .select(getACLicenseStatus)
      .subscribe((isACLicenseEnabled) => {
        this.isACLicenseEnabled = isACLicenseEnabled;
      });
    this.subscriptions.add(acLicenseSubscription);
    this.generateSettingsForm();
  }

  ngOnInit() {

    this.translateService
      .get([
        "_General_English_EN_Text_",
        "_General_Spanish_ES_Text_",
        "_General_All_Text_",
        "_Dashboard_No_Data_Available_Message_Text_",
      ])
      .subscribe((res) => {
        this.prefferedLanguagesAvailable[0].displayValue =
          res["_General_English_EN_Text_"];
        this.prefferedLanguagesAvailable[1].displayValue =
          res["_General_Spanish_ES_Text_"];
        this.allText = res["_General_All_Text_"];
        this.noDataText = res["_Dashboard_No_Data_Available_Message_Text_"];
      });

    this.dropdownSettings = {
      singleSelection: false,
      idField: "id",
      textField: "name",
      selectAllText: this.allText,
      unSelectAllText: this.allText,
      enableCheckAll: true,
      itemsShowLimit: 3,
      noDataAvailablePlaceholderText: this.noDataText,
    };

    //Set signed in users home location as the default location when loading the user grid
    const locationCodeSubscription = this.store
      .select(getLoggedInUserHomeLocationInfo)
      .subscribe((location) => {
        if (
          location.homeLocationCode !== undefined &&
          location.homeLocationCode !== null &&
          this.locationId == 0
        ) {
          if (
            location.homeLocationId == undefined ||
            location.homeLocationId == null
          ) {
            throw Error(
              "user.homeLocationId was undefined! Which was not expected!",
            );
          }
          if (
            location.homeLocationName == undefined ||
            location.homeLocationName == null
          ) {
            throw Error(
              "user.homeLocationName was undefined! Which was not expected!",
            );
          }
          this.locationId = location.homeLocationId;
          this.locationName = location.homeLocationName;
        }
      });
    this.subscriptions.add(locationCodeSubscription);
    this.subscriptions.add(
      this.store.select(getLoggedInUserId).subscribe((userId) => {
        if (userId == undefined) {
          throw Error("userId was undefined! Which was not expected!");
        }
        this._onReceiveUserId(userId);
      }),
    );
    this.userCodeAlreadyUsed = false;
    let isEdittingUserdata = this.userService.getEditingUser();

    this.subscriptions.add(
      this.tenantConfigService.getSystemConfiguration().subscribe((result) => {
        this.tenantConfiguration = result;
        if (!this.tenantConfiguration?.twoFactorEnabled)
          this.disableTFSection();
        else this.enableTFSection();
      }),
    );

    //Set editing users data
    if (isEdittingUserdata) {
      this.editingUserId = isEdittingUserdata?.userDetails?.userId;

      this.isSaveAndContinueVisible = false;
      this.userService
        .getEditingUserDetails(this.editingUserId)
        .subscribe((userData) => {
          this.userloginEnabled = this.isACLicenseEnabled
            ? userData.userDetails.loginEnabled
            : true;

          if (this.editingUserId == undefined) {
            throw Error(
              "this.editingUserId was undefined! Which was not expected!",
            );
          }

          this.lookupService
            .getPermissiongGroupsForUser(this.editingUserId)
            .subscribe({
              next: (permissionGroups) => {
                this.permissionGroupList = permissionGroups;
                this.setEditPermission(userData.userDetails.permissionGroupId);
              },
              error: (err) => {
                console.log(err);
              },
            });

          this.allUserData = userData;
          this.locationId = userData.userDetails.homeLocationId;
          this.locationName = userData.userDetails.homeLocationName;

          this.basicSettings.patchValue({
            userCode: userData.userDetails.userCode,
            firstName: userData.userDetails.firstName,
            lastName: userData.userDetails.lastName,
            userName: userData.userDetails.userName,
            permissionGroup: userData.userDetails.permissionGroupId,
            notificationGroups: [],
            prefferedLanguage: userData.userDetails.preferredLanguage,
            spanishSpeaker: userData.userDetails.isSpanishSpeaker,
            activeUser: userData.userDetails.isActive,
            supervisor: userData.userDetails.supervisorId,
            accountLocked: userData.userDetails.accountLocked,
            primaryNotificationEmail: userData.userDetails.email,
            phoneNumber: userData.userDetails.phoneNumber,
            loginEnabled: userData.userDetails.loginEnabled,
            useAuthentication: userData.userDetails.twoFactorEnabled,
            resetAtNextLogon: !userData.userDetails.twoFactorConfigured,
          });

          this.userActive = userData.userDetails.isActive;

          this.loadNotificationGroups();

          this.myProfileService.getLoggedUserProfile().subscribe({
            next: (response) => {
              response.userId == this.editingUserId
                ? this.basicSettings.controls["permissionGroup"].disable()
                : this.editingUserId;
            },
            error: (err) => {
              console.log(err);
            },
          });

          this.failedLoginCount = userData.failedAttemptsCount;
        });
    } else {
      this.userloginEnabled = this.isACLicenseEnabled ? false : true;
      this.isSaveAndContinueVisible = true;
      this.editingUserId = 0;
      this.lookupService.getPermissiongGroups().subscribe({
        next: (permissionGroups) => {
          this.permissionGroupList = permissionGroups;
        },
        error: (err) => {
          console.log(err);
        },
      });

      this.loadNotificationGroups();
    }
    this.checkForDirty();
    this.store.select(GetUserPermissionList).subscribe((allUserPermissions) => {
      if (allUserPermissions == undefined) {
        throw Error(
          "allUserPermissions was undefined! Which was not expected!",
        );
      }
      this.userPermissionList = allUserPermissions;
      if (!this.userPermissionList?.includes(21)) {
        this.basicSettings.controls["activeUser"].disable();
      }
    });
  }

  onLocationChange(locationData: LocationSelectorInfo) {
    this.dirtyEmitter.emit(true);
    if (locationData.id == undefined || locationData.id == null) {
      throw Error(
        "locationData.id was undefined or null! Which was not expected!",
      );
    }
    this.isFormDirty = true;
    this.locationId = locationData.id;
    this.locationName = locationData.locationName;
    this.basicSettings.markAsDirty();
  }
  private _onReceiveUserId(userId: number) {
    this.loggedUserId = userId;
  }

  // FormControl Getters
  get userCode() {
    return this.basicSettings.get("userCode");
  }

  get firstName() {
    return this.basicSettings.get("firstName");
  }

  get lastName() {
    return this.basicSettings.get("lastName");
  }

  get userName() {
    return this.basicSettings.get("userName");
  }

  get primaryNotificationEmail() {
    return this.basicSettings.get("primaryNotificationEmail");
  }

  get phoneNumber() {
    return this.basicSettings.get("phoneNumber");
  }
  get userGroup() {
    return this.basicSettings.get("userGroup");
  }
  get permissionGroup() {
    return this.basicSettings.get("permissionGroup");
  }
  get notificationGroups() {
    return this.basicSettings.get("notificationGroups");
  }

  /**
   * Notify if there are unsaved changed in this child form to the parent form
   */
  disableTFSection() {
    this.basicSettings.controls["resetAtNextLogon"].disable();
    this.basicSettings.controls["useAuthentication"].disable();
  }

  enableTFSection() {
    this.basicSettings.controls["resetAtNextLogon"].enable();
    this.basicSettings.controls["useAuthentication"].enable();
  }

  public checkForDirty() {
    this.basicSettings.valueChanges.subscribe((value) => {
      var dirtyValues = this.getDirtyValues(this.basicSettings);
      if (dirtyValues != undefined && Object.keys(dirtyValues).length >= 1) {
        this.dirtyEmitter.emit(true);
        this.isFormDirty = true;
      }
    });
  }

  getDirtyValues(form: any) {
    let dirtyValues: { [key: string]: any } = {};
    Object.keys(form.controls).forEach((key) => {
      if (key != "notificationGroups") {
        const currentControl = form.controls[key];
        if (currentControl.dirty) {
          if (currentControl.controls)
            dirtyValues[key] = this.getDirtyValues(currentControl);
          else dirtyValues[key] = currentControl.value;
        }
      }
    });
    return dirtyValues;
  }

  /**
   * Triggers once saving user details are successfully.
   */
  public showToast(): void {
    this.notificationService.show({
      content:
        "Your basic settings has been saved. Proceed to next tabs add other information. ",
      cssClass: "button-notification",
      animation: { type: "slide", duration: 400 },
      position: { horizontal: "center", vertical: "bottom" },
      type: { style: "success", icon: true },
      hideAfter: 200,
      closable: true,
    });
  }

  /**
   * Generated basic settings form
   */
  public generateSettingsForm() {
    //this.getHomeLocationList();
    this.basicSettings = this.fb.group({
      userCode: new FormControl("", [
        Validators.required,
        Validators.maxLength(50),
      ]),
      firstName: ["", [Validators.required, Validators.maxLength(50)]],
      lastName: ["", [Validators.required, Validators.maxLength(50)]],
      userName: ["", [Validators.required, EmailValidator()]],
      userGroup: [null],
      permissionGroup: ["", [Validators.required]],
      notificationGroups: [null, []],
      prefferedLanguage: this.prefferedLanguagesAvailable[0].id,
      activeUser: [true],
      //homeLocation: new FormControl(),
      supervisor: [null],
      spanishSpeaker: [false],
      active: [true],
      accountLocked: [false],
      loginEnabled: [this.isACLicenseEnabled ? false : true],
      useAuthentication: [false],
      resetAtNextLogon: [true],
      primaryNotificationEmail: ["", [Validators.required, EmailValidator()]],
      phoneNumber: [""],
    });
    const userGroup = this.basicSettings.get("userGroup");
  }

  /**
   * Get adding/edting users data
   */
  public getAddEditUser() {
    let userSettings = new IBasicUserSettingsModel();

    userSettings.IsActive = this.basicSettings.controls.activeUser.value;
    userSettings.UserName = this.basicSettings.controls.userName.value;
    userSettings.UserCode = this.basicSettings.controls.userCode.value;
    userSettings.HomeLocationId = this.locationId;
    userSettings.FirstName = this.basicSettings.controls.firstName.value;
    userSettings.LastName = this.basicSettings.controls.lastName.value;
    userSettings.PrimaryEmail =
      this.basicSettings.controls.primaryNotificationEmail.value;
    userSettings.PhoneNumber = this.basicSettings.controls.phoneNumber.value;
    userSettings.PreferredLanguage =
      this.basicSettings.controls.prefferedLanguage.value;
    userSettings.IsSpanishSpeaker =
      this.basicSettings.controls.spanishSpeaker.value;
    userSettings.AccountLocked =
      this.basicSettings.controls.accountLocked.value;
    userSettings.LoginEnabled = this.isACLicenseEnabled
      ? this.basicSettings.controls.loginEnabled.value
      : true;
    userSettings.PermissionGroupId =
      this.basicSettings.controls.permissionGroup.value;
    userSettings.NotificationGroups = [];
    this.basicSettings.controls.notificationGroups.value.forEach(
      (groups: any) => userSettings.NotificationGroups.push(groups.id),
    );
    userSettings.twoFactorEnabled =
      this.basicSettings.controls.useAuthentication.value;
    userSettings.twoFactorConfigured =
      !this.basicSettings.controls.resetAtNextLogon.value;
    userSettings.virtualConferencingUserId = this.userService.confUserId;
    return userSettings;
  }

  /**
   * Triggers when submitting the basic settings
   * @param event
   */
  public submitBasicSettings() {
    this.dirtyEmitter.emit(false);
    let userSettings = this.getAddEditUser();
    let isEdittingUserdata = this.userService.getEditingUser(); // initial user data
    if (isEdittingUserdata) {
      userSettings.UserId = isEdittingUserdata?.userDetails?.userId;
      this.editingUserId = isEdittingUserdata?.userDetails?.userId;
    } else {
      userSettings.UserId = 0;
      userSettings.twoFactorConfigured = false;
    }

    if (isEdittingUserdata) {
      if (this.editingUserId == undefined) {
        throw Error(
          "this.editingUserId was undefined! Which was not expected!",
        );
      }
      this.userService
        .updateBasicUserSettings(userSettings, this.editingUserId)
        .subscribe(
          (data) => {
            this.userService
              .getEditingUserDetails(this.editingUserId!)
              .subscribe(
                (userData) => {
                  this.userService.setEditingUser(userData);
                  this.toastService.showSuccess(
                    "_General_Changes_Saved_Successfully_Ts_Text_",
                  );
                  this.basicSettings.markAsPristine();
                  this.dirtyEmitter.emit(false);
                },
                (error) => {
                  console.log(error);
                  this.toastService.showError(
                    "_General_Saving_Failed_Ts_Text_",
                  );
                  this.dirtyEmitter.emit(true);
                },
              );
            this.submittedEmitter.emit(false);
          },
          (error) => {
            console.log(error);
            this.toastService.showError("_General_Saving_Failed_Ts_Text_");
          },
        );
    } else {
      this.userService.addUser(userSettings).subscribe(
        (data) => {
          this.userService.getEditingUserDetails(data.id).subscribe(
            (userData) => {
              this.userService.setEditingUser(userData);
              this.toastService.showSuccess("_User_User_Created_Ts_Text_");
              this.basicSettings.markAsPristine();
              this.dirtyEmitter.emit(false);
              this.backToUserGrid();
            },
            (error) => {
              console.log(error);
              this.toastService.showError(
                "_User_User_Creation_Failed_Ts_Text_",
              );
              this.dirtyEmitter.emit(true);
            },
          );
          this.submittedEmitter.emit(false);
        },
        (error) => {
          console.log(error);
          this.toastService.showError("_User_User_Creation_Failed_Ts_Text_");
        },
      );
    }
  }

  /**
   * Save user data synchronously
   */
  public saveUserSynchronous() {
    let userSettings = this.getAddEditUser();
    userSettings.UserId = 0;
    userSettings.twoFactorConfigured = false;
    this.spinner.show("user-spinner");
    this.userService.addUserSynchronous(userSettings).subscribe(
      (data) => {
        this.userService.getEditingUserDetails(data.id).subscribe(
          (userData) => {
            this.userService.setEditingUser(userData);
            this.spinner.hide("user-spinner");
            this.toastService.showSuccess("_User_User_Created_Ts_Text_");
            this.isSaveAndContinueVisible = false;
            this.editingUser = this.userService.getEditingUser();
            this.editingUserId = this.editingUser?.userDetails.userId;
            this.basicSettings.markAsPristine();
            this.isFormDirty = false;
            this.updateManageUserSettingPage();
          },
          (error) => {
            console.log(error);
            this.spinner.hide("user-spinner");
            this.toastService.showError("_User_User_Creation_Failed_Ts_Text_");
          },
        );
        this.submittedEmitter.emit(false);
      },

      (error) => {
        console.log(error);
        this.spinner.hide("user-spinner");
        this.toastService.showError("_User_User_Creation_Failed_Ts_Text_");
      },
    );
  }

  public backToUserGrid() {
    this.router.navigate(["admin/user"]);
  }

  public languageTrackBy(index: number, item: IListItemModel) {
    return item.id;
  }

  /**
   * Triggers when user enters a new username
   * Check for availability
   */
  public onFocusOutUserName() {
    if (this.basicSettings.controls.userName.value) {
      if (this.editingUserId == undefined) {
        throw Error(
          "this.editingUserId was undefined! Which was not expected!",
        );
      }
      this.userService
        .checkUserNameExists(
          this.editingUserId,
          this.basicSettings.controls.userName.value,
        )
        .subscribe((response) => {
          this.userNameAlreadyUsed =
            !response.valid && response.error == UserValidationErrors.UserName
              ? true
              : false;
        });
    }
  }

  /**
   * Change the userNameAlreadyUsed value back to false when changing the username value
   */
  public onChangeUserName() {
    this.userNameAlreadyUsed = false;
  }

  /**
   * Triggers when user enters a new usercode
   * Check for availability
   */
  public onFocusOutUserCode() {
    if (this.basicSettings.controls.userCode.value) {
      if (this.editingUserId == undefined) {
        throw Error(
          "this.editingUserId was undefined! Which was not expected!",
        );
      }
      this.userService
        .checkUserCodeExists(
          this.editingUserId,
          this.basicSettings.controls.userCode.value,
        )
        .subscribe((response) => {
          this.userCodeAlreadyUsed =
            !response.valid && response.error == UserValidationErrors.UserCode
              ? true
              : false;
        });
    }
  }

  /**
   * Change the userCodeAlreadyUsed value back to false when changing the user code value
   */
  public onChangeUserCode() {
    this.userCodeAlreadyUsed = false;
  }

  /**
   * Triggers when user enters a new primary email
   * Check for availability
   */
  public onFocusOutPrimaryEmail() {
    if (this.basicSettings.controls.primaryNotificationEmail.value) {
      if (this.editingUserId == undefined) {
        throw Error(
          "this.editingUserId was undefined! Which was not expected!",
        );
      }
      this.userService
        .checkUserEmailExists(
          this.editingUserId,
          this.basicSettings.controls.primaryNotificationEmail.value,
        )
        .subscribe((response) => {
          this.primaryNotificationEmailAlreadyUsed =
            !response.valid && response.error == UserValidationErrors.Email
              ? true
              : false;
        });
    }
  }

  /**
   * Change the primaryNotificationEmailAlreadyUsed value back to false when changing the primary email value
   */
  public onChangeUserEmail() {
    this.primaryNotificationEmailAlreadyUsed = false;
  }

  /**
   * Triggers when user clicked on the reset
   * password button.
   */
  public sendResetPasswordEmail() {
    let userObject = new PasswordResetRequestModel();
    if (this.allUserData != null) {
      userObject.Email = this.allUserData.userDetails.email;
      userObject.FirstName = this.allUserData.userDetails.firstName;
      userObject.UserName = this.allUserData.userDetails.userName;
      userObject.RedirectUri = this.userService.getReturnUrl();
      this.userService.sendResetPassowrdEmail(userObject).subscribe(
        (value) => {
          this.resetClickedByUser();
          /*Assuming that password reset call gives response of the reset password link */
          this.passwordResetLink = value;
          this.toastService.showSuccess(
            "_User_Password_Reset_Request_Sent_Ts_Text_",
          );
        },
        (error) => {
          this.toastService.showError(
            "_User_Password_Reset_Request_Sent_Failed_Ts_Text_",
          );
        },
      );
    } else {
      userObject.Email = this.basicSettings?.get(
        "primaryNotificationEmail",
      )?.value;
      userObject.FirstName = this.basicSettings?.get("firstName")?.value;
      userObject.UserName = this.basicSettings?.get("userName")?.value;
    }
  }

  /**
   * Toggles the enabled disbaled property of the link
   */
  public resetClickedByUser() {
    this.isResetClicked = true;
  }

  /**
   * Triggerd when Account Locked
   * Set the failed login attamps count to 0 if Account Locked is checked
   */
  public unlockAccount() {
    if (!this.basicSettings.get("accountLocked")?.value) {
      this.failedLoginCount = 0;
    }
  }

  public loginStatusChange(value: any) {
    this.userloginEnabled = value.currentTarget.checked;
    if (!this.userloginEnabled && !this.tenantConfiguration?.twoFactorEnabled) {
      this.basicSettings.patchValue({
        resetAtNextLogon: true,
        useAuthentication: false,
      });
    } else {
      let isEdittingUserdata = this.userService.getEditingUser(); // initial user data
      this.basicSettings.patchValue({
        resetAtNextLogon: isEdittingUserdata ? false : true,
        useAuthentication: false,
      });
    }
  }

  public activeStatusChange() {
    this.userActive = this.basicSettings.get("activeUser")?.value;
  }

  /**
   * Shows the users the password reset link
   */
  public onToggle(): void {
    this.show = !this.show;
  }

  public close(status: string) {
    this.show = false;
  }

  public open() {
    this.show = true;
  }

  /**
   * Executes the functionality related to copy to clipboard
   */
  public copyInputMessage(val: string) {
    const selBox = document.createElement("textarea");
    selBox.style.position = "fixed";
    selBox.style.left = "0";
    selBox.style.top = "0";
    selBox.style.opacity = "0";
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand("copy");
    document.body.removeChild(selBox);
    this.toastService.showSuccess("_User_Password_Reset_Link_Copied_Ts_Text_");
  }

  /**
   * Close the accordion panel without reverting the changes when clicking on the Close button
   */
  public cancelClicked() {
    this.closeEmitter.emit("ngb-panel-0");
  }

  public onFocusOutPhoneNumber() {
    if (
      this.basicSettings.get("phoneNumber")?.value.length > 0 &&
      this.basicSettings.get("phoneNumber")?.value.length < 10
    ) {
      this.invalidPhoneNumber = true;
    } else {
      this.invalidPhoneNumber = false;
    }
  }

  public onPermissionGroupChange() {
    this.userloginEnabled = this.basicSettings.get("loginEnabled")?.value;
    this.setEditPermission(this.basicSettings.controls.permissionGroup.value);
  }

  private setEditPermission(permisionGroupId: number) {
    this.permissionGroupId = permisionGroupId;
    this.basicSettings.controls["loginEnabled"].enable();
  }

  private loadNotificationGroups(): void {
    this.lookupService.getNotificationGroups().subscribe({
      next: (notificationGroups) => {
        this.notificationGroupList = notificationGroups;
        this.selectedNotificationGroupList = [];
        this.allUserData?.userDetails?.notificationGroups?.forEach((group) => {
          var notificationGroup = this.notificationGroupList.find(
            (g) => g.id == group,
          );
          if (notificationGroup) {
            this.selectedNotificationGroupList.push(notificationGroup);
          }
        });
      },
      error: (err) => {
        console.log(err);
      },
    });
  }
  onItemSelect(data: any) {
    this.dirtyEmitter.emit(true);
    this.isFormDirty = true;
  }

  public isUserCodeRequired(userCode: any): boolean {
    return userCode?.invalid && (userCode?.dirty || userCode?.touched);
  }

  public isUserCodeAlreadyInUse(userCode: any): boolean {
    return userCode.dirty || userCode.touched;
  }

  public isFirstNameRequired(firstName: any): boolean {
    return firstName.invalid && (firstName.dirty || firstName.touched);
  }

  public isLastNameRequired(lastName: any): boolean {
    return lastName.invalid && (lastName.dirty || lastName.touched);
  }

  public isUsernameRequired(userName: any): boolean {
    return userName.invalid && (userName.dirty || userName.touched);
  }

  public isInvalidUsername(userName: any): boolean {
    return userName.invalid;
  }

  public isUsernameAlreadyInUse(userName: any): boolean {
    return userName.dirty || userName.touched;
  }

  public isPermissionGroupRequired(permissionGroup: any): boolean {
    return permissionGroup.invalid && permissionGroup.touched;
  }

  public isPrimaryNotificationEmailRequired(
    primaryNotificationEmail: any,
  ): boolean {
    return (
      primaryNotificationEmail.invalid &&
      (primaryNotificationEmail.dirty || primaryNotificationEmail.touched)
    );
  }

  public isPrimaryNotificationEmailInvalid(
    primaryNotificationEmail: any,
  ): boolean {
    return primaryNotificationEmail.invalid;
  }

  public isSaveButtonDisabled(): boolean {
    return (
      !this.basicSettings.valid ||
      !this.basicSettings.dirty ||
      this.userCodeAlreadyUsed ||
      !this.isFormDirty ||
      this.userNameAlreadyUsed ||
      this.invalidPhoneNumber
    );
  }

  public isSaveAndContinueButtonDisabled(): boolean {
    return (
      !this.basicSettings.valid ||
      !this.basicSettings.dirty ||
      this.userCodeAlreadyUsed ||
      this.userNameAlreadyUsed ||
      this.invalidPhoneNumber
    );
  }
}
